/**
 * Vanilla Cookie Banner
 *
 * Bottom-anchored, non-blocking cookie banner rendered as plain DOM.
 * Lives in BOTH layouts (Devise + React SPA) because GDPR requires
 * consent to be captured on the first visit, which is often an
 * unauthenticated Devise page before React boots.
 *
 * Three equally-prominent actions: Accept All / Reject All / Customize.
 * No "X" close - the banner is dismissed only by an explicit choice.
 * Customize delegates to the shared core's `showCustomize()` so the
 * vanilla modal (`customizeModal.ts`) can react and mount. Customize is
 * gated (`disabled` + `aria-disabled`, paint-only) until `defaultsSettled`
 * via `syncGate()`; Accept All and Reject All are never gated.
 */

import {
  getState,
  save,
  showCustomize,
  type ConsentCategories,
  type ConsentSource,
  type ConsentState,
} from "./core";

export interface BannerMountOptions {
  privacyPolicyHref?: string;
}

export interface BannerHandle {
  unmount: () => void;
}

const BANNER_ELEMENT_ID = "as-cookie-banner";

/**
 * Mount the banner into the given container. Calling `mountBanner`
 * twice is a no-op; the existing handle is returned instead so the
 * entrypoint can idempotently react to core state transitions.
 *
 * @param container - usually `document.body`; defaults as such
 * @param options - optional overrides (e.g. a custom privacy href)
 * @returns a handle exposing `unmount()` so subscribers can remove the
 *   banner when state flips to `hasDecided: true`
 */
export function mountBanner(
  container: HTMLElement = document.body,
  options: BannerMountOptions = {},
): BannerHandle {
  const existing = document.getElementById(BANNER_ELEMENT_ID);
  if (existing) {
    return { unmount: () => existing.remove() };
  }

  const privacyHref = options.privacyPolicyHref ?? "/privacy-policy";

  const root = document.createElement("section");
  root.id = BANNER_ELEMENT_ID;
  root.setAttribute("role", "region");
  root.setAttribute("aria-label", "Cookie consent");
  root.setAttribute("aria-live", "polite");
  root.className =
    "fixed bottom-0 inset-x-0 z-[9999] px-4 pb-4 sm:pb-6 sm:px-6 motion-safe:animate-[fadeIn_0.2s_ease-out] pointer-events-none";

  const card = document.createElement("div");
  card.className =
    "pointer-events-auto mx-auto max-w-4xl rounded-xl border border-border-base bg-surface-card p-4 sm:p-6 shadow-xl";
  root.appendChild(card);

  const content = document.createElement("div");
  content.className =
    "flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between";
  card.appendChild(content);

  const copy = document.createElement("div");
  copy.className = "flex-1";
  const heading = document.createElement("h2");
  heading.className = "text-base font-semibold text-text-title";
  heading.textContent = "We use cookies";
  copy.appendChild(heading);

  const body = document.createElement("p");
  body.className = "mt-2 text-sm text-text-label";
  body.innerHTML =
    "We use Strictly Necessary cookies to sign you in, keep your session secure, remember your preferences, " +
    "and monitor errors (Sentry, server-side only). Optional analytics cookies are off by default. " +
    'Read our <a class="font-medium text-text-teal-700 underline hover:text-text-teal-800" href="' +
    privacyHref +
    '">Privacy Policy</a>.';
  copy.appendChild(body);
  content.appendChild(copy);

  const actions = document.createElement("div");
  actions.className =
    "flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:shrink-0";
  content.appendChild(actions);

  actions.appendChild(
    buildButton({
      label: "Reject All",
      variant: "secondary",
      onClick: () =>
        handleDecision(
          { necessary: true, analytics: false, advertising: false },
          "banner_reject_all",
        ),
      ariaLabel: "Reject all optional cookies",
    }),
  );
  actions.appendChild(
    buildButton({
      label: "Customize",
      variant: "secondary",
      onClick: () => showCustomize(),
      ariaLabel: "Customize cookie preferences",
      gated: true,
    }),
  );
  actions.appendChild(
    buildButton({
      label: "Accept All",
      variant: "primary",
      onClick: () =>
        handleDecision(
          { necessary: true, analytics: true, advertising: true },
          "banner_accept_all",
        ),
      ariaLabel: "Accept all cookies",
    }),
  );

  syncGateIn(root, getState());
  container.appendChild(root);

  return {
    unmount: () => {
      const node = document.getElementById(BANNER_ELEMENT_ID);
      node?.remove();
    },
  };
}

type ButtonVariant = "primary" | "secondary";

interface ButtonOptions {
  label: string;
  variant: ButtonVariant;
  onClick: () => void;
  ariaLabel?: string;
  /** Disabled until the defaults settle (see `syncGate`). */
  gated?: boolean;
}

/**
 * Enable or disable the banner's gated controls from `defaultsSettled`.
 * No-op when the banner is not mounted.
 *
 * @param state - current `ConsentState` snapshot
 */
export function syncGate(state: ConsentState): void {
  const root = document.getElementById(BANNER_ELEMENT_ID);
  if (root) syncGateIn(root, state);
}

/**
 * Apply the gate to every `[data-consent-gate]` control under `root`.
 *
 * @param root - the banner root (mounted or about to be)
 * @param state - current `ConsentState` snapshot
 */
function syncGateIn(root: HTMLElement, state: ConsentState): void {
  const gated = !state.defaultsSettled;
  root
    .querySelectorAll<HTMLButtonElement>("[data-consent-gate]")
    .forEach((btn) => {
      btn.disabled = gated;
      if (gated) btn.setAttribute("aria-disabled", "true");
      else btn.removeAttribute("aria-disabled");
    });
}

/**
 * Build one of the three banner buttons. Accept/Reject/Customize all
 * share the same size and padding so neither decision is visually
 * privileged (EDPB Guidelines 05/2020).
 *
 * @param opts - visible label, variant, click handler
 * @returns the button element
 */
function buildButton(opts: ButtonOptions): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = opts.label;
  if (opts.ariaLabel) btn.setAttribute("aria-label", opts.ariaLabel);

  const base =
    "inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium " +
    "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card " +
    "w-full sm:w-auto";
  const variant =
    opts.variant === "primary"
      ? " bg-fixed-teal-600 text-white hover:bg-fixed-teal-700 focus-visible:ring-fixed-teal-500"
      : " border border-border-control bg-button-neutral-bg text-text-emphasis hover:bg-surface-alt focus-visible:ring-fixed-teal-500";
  btn.className =
    base +
    variant +
    (opts.gated ? " disabled:opacity-50 disabled:cursor-not-allowed" : "");
  if (opts.gated) btn.setAttribute("data-consent-gate", "");

  btn.addEventListener("click", opts.onClick);
  return btn;
}

/**
 * Handle an Accept/Reject click: forward to the core's `save()` which
 * persists the decision, dispatches `consent:change`, and causes the
 * entrypoint subscriber to unmount the banner.
 *
 * @param categories - the category selection for this decision
 * @param source - `banner_accept_all` or `banner_reject_all`
 */
function handleDecision(
  categories: ConsentCategories,
  source: ConsentSource,
): void {
  void save(categories, source);
}
